import {
	useMutation,
	useQueryClient,
	type QueryKey,
} from "@tanstack/react-query";
import { getLogger } from "@/infrastructure/di";

interface CreateMutationConfig<TData, TError, TVariables> {
	mutationFn: (variables: TVariables) => Promise<TData>;
	invalidateKeys?:
		| QueryKey[]
		| ((data: TData, variables: TVariables) => QueryKey[]);
	onSuccess?: (data: TData, variables: TVariables, context: unknown) => void;
	onError?: (error: TError, variables: TVariables, context: unknown) => void;
	errorLogMessage?: string;
}

/**
 * Hook factory that wraps useMutation with standardised invalidation and logging.
 * Internal to application/queries — not exported from the barrel.
 */
export function useCreateMutation<
	TData = unknown,
	TError = Error,
	TVariables = void,
>(config: CreateMutationConfig<TData, TError, TVariables>) {
	const queryClient = useQueryClient();

	return useMutation<TData, TError, TVariables>({
		mutationFn: config.mutationFn,
		onSuccess: (data, variables, context) => {
			if (config.invalidateKeys) {
				const keys =
					typeof config.invalidateKeys === "function"
						? config.invalidateKeys(data, variables)
						: config.invalidateKeys;
				for (const key of keys) {
					queryClient.invalidateQueries({ queryKey: key });
				}
			}
			config.onSuccess?.(data, variables, context);
		},
		onError: (error, variables, context) => {
			getLogger().error(config.errorLogMessage ?? "Mutation failed:", error);
			config.onError?.(error, variables, context);
		},
	});
}
